import React from 'react';

import {Avatar, FormControl, ListItemAvatar, ListItemText, MenuItem, TextField} from '@mui/material';
import {Groups as GroupsIcon} from '@mui/icons-material';
import {useQuery} from 'react-query';

import API, {Types} from '@wheeebo/polaris-api';
import {CircleLoader, LoadError, useErrorHandler} from '@wheeebo/polaris-ui';

interface GroupSelectorProps {
  locked?: boolean;
  selectedGroup?: Types.Group;
  excludeGroups?: Types.Group[];
  setSelectedGroup: (group: Types.Group) => void;
}

function GroupSelector({locked = false, selectedGroup, excludeGroups, setSelectedGroup}: GroupSelectorProps) {
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const [options, setOptions] = React.useState<Types.Group[]>();
  const {data, isError, isLoading} = useQuery(API.Groups.listKey({}), () => API.Groups.list({}), {
    onError: showAndEmitJsErrorEvent,
  });

  React.useEffect(() => {
    if (!data?.items) {
      return;
    }
    setOptions(data.items.filter((e: Types.Group) => !excludeGroups?.find((g) => g.id === e.id)) || []);
  }, [data?.items, excludeGroups]);

  return (
    <FormControl fullWidth>
      {isLoading ? <CircleLoader /> : null}
      {isError ? <LoadError /> : null}
      {options ? (
        <TextField
          disabled={locked || !options || options.length === 0}
          id="group-select"
          label="Group"
          onChange={(event) => setSelectedGroup(options.filter((e) => e.id === event.target.value)[0])}
          select
          fullWidth
          inputProps={{
            sx: {display: 'flex', alignItems: 'center'},
          }}
          // Explicitly set an empty string (instead of leaving it as undefined)
          // to avoid a uncontrolled comoponent error.
          value={selectedGroup?.id || ''}>
          {options?.map((e: Types.Group) => (
            <MenuItem key={e.id} value={e.id}>
              <ListItemAvatar>
                <Avatar src={e.imageUrl} variant="rounded" sx={{width: 28, height: 28, backgroundColor: 'transparent'}}>
                  <GroupsIcon color="action" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={e.name} />
            </MenuItem>
          ))}
        </TextField>
      ) : null}
    </FormControl>
  );
}

export default GroupSelector;
